"use client";

import * as React from "react";

/** Scroll distance (px) before the nav leaves its initial transparent state. */
const PAGE_TOP_THRESHOLD = 8;

export interface HeroOverlayNavState {
  /** Hero sentinel still occupies the fixed nav band. */
  isOverHero: boolean;
  /** User has not scrolled past {@link PAGE_TOP_THRESHOLD}. */
  isAtPageTop: boolean;
}

/**
 * Tracks hero-overlay nav state for pages with a dark full-bleed hero.
 *
 * - `isAtPageTop` — transparent nav only while scrollY ≤ threshold (VsNav pattern).
 * - `isOverHero` — false once the hero scrolls above the nav band (IntersectionObserver).
 */
export function useHeroOverlayNav(
  enabled: boolean,
  selector = "[data-hero-overlay-nav]"
): HeroOverlayNavState {
  const [isOverHero, setIsOverHero] = React.useState(enabled);
  const [isAtPageTop, setIsAtPageTop] = React.useState(true);

  React.useEffect(() => {
    if (!enabled) {
      setIsOverHero(false);
      setIsAtPageTop(true);
      return;
    }

    const hero = document.querySelector(selector);
    if (!hero) {
      setIsOverHero(false);
      setIsAtPageTop(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsOverHero(entry.isIntersecting);
      },
      {
        threshold: 0,
        // Nav is 68px mobile / 72px desktop — treat the hero as "past" once
        // it no longer occupies the header band.
        rootMargin: "-72px 0px 0px 0px",
      }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, [enabled, selector]);

  React.useEffect(() => {
    if (!enabled) {
      setIsAtPageTop(true);
      return;
    }

    const frameRef = { current: null as number | null };

    const compute = () => {
      frameRef.current = null;
      setIsAtPageTop(window.scrollY <= PAGE_TOP_THRESHOLD);
    };

    const schedule = () => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [enabled]);

  return { isOverHero, isAtPageTop };
}
